import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import './CareerScoresPage.css';
import { FaPrint, FaChevronDown, FaArrowRight } from 'react-icons/fa';
import logo from '../../assets/OG-logo-main-learning-scores.png';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// Same threshold the dashboard uses for strengths vs growth areas.
const STRENGTH_THRESHOLD = 3.5;
const STRENGTH_COLOR = '#2ed573';
const GROWTH_COLOR = '#ffa502';

// ── Snapshot sub-components ────────────────────────────────────────────────
const SummaryChip = ({ label, value, sub, accent }) => (
  <div className="cs-chip" style={{ borderColor: `${accent}55` }}>
    <span className="cs-chip-label">{label}</span>
    <span className="cs-chip-value" style={{ color: accent }}>{value}</span>
    {sub && <span className="cs-chip-sub">{sub}</span>}
  </div>
);

const FacetRow = ({ facet, accent, expandable, defaultOpen, onOpenCoachXcel }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const pct = Math.min(100, (facet.score / 5) * 100);
  const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(
    `best ways to improve ${facet.name} coaching skills`
  )}`;

  return (
    <div className="cs-facet-row" style={{ borderColor: `${accent}40` }}>
      <button
        type="button"
        className="cs-facet-head"
        onClick={() => expandable && setOpen((o) => !o)}
        aria-expanded={expandable ? open : undefined}
        style={{ cursor: expandable ? 'pointer' : 'default' }}
      >
        <span className="cs-facet-dot" style={{ background: `${accent}22`, color: accent, borderColor: `${accent}55` }}>
          {facet.name?.charAt(0)}
        </span>
        <span className="cs-facet-main">
          <span className="cs-facet-line">
            <span className="cs-facet-name">{facet.name}</span>
            <span className="cs-facet-score">{facet.score.toFixed(1)}/5</span>
          </span>
          <span className="cs-bar"><span style={{ width: `${pct}%`, background: accent }} /></span>
        </span>
        {expandable && (
          <FaChevronDown className="cs-chevron" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
        )}
      </button>

      {expandable && (
        <div className="cs-detail" style={{ display: open ? 'block' : 'none' }}>
          {facet.description ? (
            <p className="cs-detail-text">{facet.description}</p>
          ) : (
            <p className="cs-detail-text" style={{ fontStyle: 'italic' }}>No description available for this facet.</p>
          )}
          <div className="cs-actions">
            <button type="button" className="cs-action-primary" onClick={onOpenCoachXcel}>
              Build a development plan <FaArrowRight size={11} />
            </button>
            <a className="cs-action-link" href={searchUrl} target="_blank" rel="noopener noreferrer">
              Research tips ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

const CoachScoresPage = ({ userId }) => {
  const [visualizations, setVisualizations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const chartRefs = useRef({});
  const CACHE_KEY = `visualizations_${userId}_Coach`;

  const queryParams = new URLSearchParams(location.search);
  const viewingChildId = location.state?.viewingChildId || queryParams.get('viewingChildId') || null;
  const viewingChildName = location.state?.childName || decodeURIComponent(queryParams.get('childName') || '') || null;

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setError('No user ID provided');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setError('Unauthorized');
      navigate('/login');
      return;
    }

    // Receive visualizations from dashboard
    if (location.state?.visualizations) {
      setVisualizations(location.state.visualizations);
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(location.state.visualizations));
      setLoading(false);
      return;
    }

    // Fallback to cache
    const cachedData = sessionStorage.getItem(CACHE_KEY);
    if (cachedData) {
      try {
        setVisualizations(JSON.parse(cachedData));
        setLoading(false);
        return;
      } catch (err) {
        sessionStorage.removeItem(CACHE_KEY);
      }
    }

    setError('No CoachReady visualization data available. Please complete the CoachReady assessment first.');
    setLoading(false);
  }, [userId, navigate, location.state]);

  useEffect(() => {
    document.body.classList.add('specific-page-coachdashboard');
    return () => document.body.classList.remove('specific-page-coachdashboard');
  }, []);

  const handlePrint = () => {
    document.body.classList.add('printing');
    window.print();
    setTimeout(() => document.body.classList.remove('printing'), 1000);
  };

  // ── Facet-level summary derived from the visualizations we already receive,
  // so no extra navigation state or API call is needed. A facet's score is the
  // average of its non-zero values across category datasets.
  const { facets, strengths, growthAreas, overallAvg } = useMemo(() => {
    if (!visualizations?.labels || !visualizations?.datasets) {
      return { facets: [], strengths: [], growthAreas: [], overallAvg: null };
    }
    const notesMap = visualizations.notesMap || {};
    const list = visualizations.labels
      .map((label, i) => {
        const vals = visualizations.datasets
          .map((d) => d.data?.[i])
          .filter((v) => typeof v === 'number' && v > 0);
        if (!vals.length) return null;
        return {
          name: label,
          score: Number((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1)),
          description: notesMap[label] || '',
        };
      })
      .filter(Boolean);

    const s = list.filter((f) => f.score >= STRENGTH_THRESHOLD).sort((a, b) => b.score - a.score);
    const g = list.filter((f) => f.score < STRENGTH_THRESHOLD).sort((a, b) => a.score - b.score);
    const avg = list.length ? list.reduce((sum, f) => sum + f.score, 0) / list.length : null;
    return { facets: list, strengths: s, growthAreas: g, overallAvg: avg };
  }, [visualizations]);

  const openCoachXcel = () =>
    navigate('/CoachTheCoachDashboard', {
      state: {
        sidebarItem: 'coachxcel',
        fromCoachScores: true,
        ...(viewingChildId && { viewingChildId, childName: viewingChildName }),
      },
    });

  const getRadarOptions = (isSingleDimension = false) => ({
    scales: {
      r: {
        beginAtZero: true,
        suggestedMax: 5,
        ticks: {
          stepSize: 1,
          color: 'white',
          backdropColor: 'transparent',
          font: { size: window.innerWidth <= 850 ? 11 : 16 },
        },
        pointLabels: {
          color: 'white',
          font: { size: window.innerWidth <= 850 ? 12 : 12 },
          padding: isSingleDimension ? 20 : 12,
          callback: (label) => (label?.length > 45 ? label.substring(0, 42) + '...' : label),
        },
        grid: { color: 'rgba(255, 255, 255, 0.35)', circular: false },
        angleLines: {
          color: 'rgba(255, 255, 255, 0.4)',
          lineWidth: isSingleDimension ? 2 : 1,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (context) => `${context.dataset.label}: ${context.raw.toFixed(1)}` },
      },
    },
    maintainAspectRatio: false,
    elements: {
      point: {
        radius: isSingleDimension ? 8 : 5,
        hoverRadius: isSingleDimension ? 10 : 7,
        backgroundColor: isSingleDimension ? '#00beb7' : undefined,
      },
    },
  });

  // Overview radar: all categories overlaid, legend on when there is more than one.
  const getOverviewRadarOptions = () => {
    const base = getRadarOptions(false);
    const mobile = window.innerWidth <= 850;
    return {
      ...base,
      plugins: {
        ...base.plugins,
        legend: {
          display: (visualizations?.datasets?.length || 0) > 1,
          position: 'top',
          labels: { color: 'white', padding: 16, font: { size: mobile ? 11 : 12 } },
        },
      },
    };
  };

  const filterValidData = (chartData) => {
    if (!chartData?.labels || !chartData?.datasets?.[0]) return null;

    const dataPoints = chartData.datasets[0].data || [];
    const validIndices = dataPoints
      .map((score, idx) => (score > 0 ? idx : -1))
      .filter((idx) => idx !== -1);

    if (validIndices.length === 0) return null;

    return {
      labels: validIndices.map((idx) => chartData.labels[idx]),
      datasets: [
        {
          ...chartData.datasets[0],
          data: validIndices.map((idx) => chartData.datasets[0].data[idx]),
        },
      ],
      notesMap: chartData.notesMap || {},
      facetNotes: validIndices.map(
        (idx) =>
          chartData.datasets[0].facetNote?.[idx] || chartData.notesMap?.[chartData.labels[idx]] || ''
      ),
      isSingleDimension: validIndices.length === 1,
    };
  };

  const renderSortedScoresTable = (filteredData) => {
    if (!filteredData?.labels || filteredData.labels.length === 0) {
      return <p style={{ color: '#aaa' }}>No scored facets in this category.</p>;
    }

    const scores = filteredData.labels
      .map((label, idx) => ({
        label,
        score: filteredData.datasets[0].data[idx] || 0,
        facetNote: filteredData.facetNotes?.[idx] || filteredData.notesMap?.[label] || '',
      }))
      .sort((a, b) => b.score - a.score);

    return (
      <table
        className="scores-table table-coach"
        style={{
          width: '100%',
          color: 'white',
          fontSize: '14px',
          borderCollapse: 'collapse',
          background: 'linear-gradient(to right, #0b1021, rgb(30, 37, 58))',
        }}
      >
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '12px' }}>Facet</th>
            <th style={{ textAlign: 'right', padding: '12px' }}>Score</th>
          </tr>
        </thead>
        <tbody>
          {scores.map((item, idx) => (
            <React.Fragment key={idx}>
              <tr>
                <td style={{ padding: '10px 12px' }}>{item.label}</td>
                <td style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 'bold' }}>
                  {item.score.toFixed(1)}
                </td>
              </tr>
              {item.facetNote && (
                <tr>
                  <td
                    colSpan="2"
                    style={{
                      padding: '4px 12px 12px',
                      color: '#00a1c9',
                      fontSize: '13px',
                      lineHeight: '1.45',
                    }}
                  >
                    {item.facetNote}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
  };

  // Print styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        .radar-container { display: none !important; }
        .chart-section { page-break-inside: avoid; }
        .scores-table { page-break-inside: avoid; }
        .header-buttons .print-button { display: none; }
        .cs-actions, .cs-chevron { display: none !important; }
        .cs-detail { display: block !important; }
        .cs-overview-grid { display: block !important; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const renderChart = (categoryName, originalData) => {
    const filteredData = filterValidData(originalData);
    if (!filteredData) return null;

    const isSingle = filteredData.isSingleDimension;

    return (
      <div className="chart-section" style={{ margin: '50px 0', width: '100%' }}>
        <h3
          style={{
            color: '#00beb7',
            textAlign: 'center',
            marginBottom: '35px',
            fontSize: '23px',
          }}
        >
          {categoryName}
        </h3>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '40px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
          }}
        >
          {/* Radar Chart */}
          <div
            className="radar-container radar-coach"
            style={{
              height: window.innerWidth <= 850 ? '520px' : isSingle ? '460px' : '500px',
              flex: '1 1 480px',
              maxWidth: isSingle ? '720px' : '760px',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <Radar
              ref={(el) => {
                if (el) chartRefs.current[categoryName] = el.chart;
              }}
              data={filteredData}
              options={getRadarOptions(isSingle)}
            />
          </div>

          {/* Scores Table */}
          <div style={{ flex: '1 1 380px', maxWidth: '620px', minWidth: 0, boxSizing: 'border-box', overflowWrap: 'anywhere' }}>
            {renderSortedScoresTable(filteredData)}
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div style={{ color: 'white', padding: '80px', textAlign: 'center' }}>Loading CoachReady scores...</div>;
  if (error) return <div style={{ color: '#ffaa00', padding: '80px', textAlign: 'center' }}>{error}</div>;
  if (!visualizations) {
    return <div style={{ color: 'white', padding: '80px', textAlign: 'center' }}>No CoachReady data available.</div>;
  }

  const showSnapshot = facets.length > 0;

  return (
    <div className="careerscores">
      <style>{`
        .cs-snapshot { width: min(1400px, 96vw); margin: 30px auto 0; }
        .cs-section-title { color: #028BE8; font-size: 23px; text-align: center; margin: 0 0 6px; }
        .cs-section-sub { color: #9aa5b4; font-size: 13.5px; text-align: center; margin: 0 0 24px; }

        .cs-chips { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-bottom: 24px; }
        .cs-chip {
          display: flex; flex-direction: column; align-items: center; gap: 2px;
          min-width: 170px; padding: 12px 20px; border-radius: 12px;
          background: rgba(255,255,255,0.03); border: 1px solid;
        }
        .cs-chip-label { font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: #9aa5b4; }
        .cs-chip-value { font-size: 26px; font-weight: 800; line-height: 1.1; }
        .cs-chip-sub { font-size: 12px; color: #9aa5b4; }

        .cs-overview-grid { display: flex; gap: 24px; align-items: flex-start; justify-content: center; }
        .cs-radar-card {
          flex: 1 1 0; minWidth: 460px; height: 590px; padding: 10px;
          background: linear-gradient(135deg, rgba(0,190,183,0.08), rgba(15,22,41,0.4));
          border: 1px solid rgba(0,190,183,0.35); border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.35);
        }
        .cs-side { width: 360px; flex-shrink: 0; display: flex; flex-direction: column; gap: 20px; }
        .cs-panel { width: 360px; height: 590px; background: rgba(255,255,255,0.02); border: 1px solid rgba(0,190,183,0.3); border-radius: 16px; padding: 16px; }
        .cs-panel-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; margin: 0 0 4px; }
        .cs-panel-hint { font-size: 12px; color: #8793a3; margin: 0 0 12px; }
        .cs-panel-list { display: flex; flex-direction: column; gap: 8px; }
        .cs-empty { font-size: 13px; color: #9aa5b4; font-style: italic; margin: 4px 0 0; }

        .cs-facet-row { border: 1px solid; border-radius: 10px; background: rgba(255,255,255,0.03); overflow: hidden; }
        .cs-facet-head {
          display: flex; align-items: center; gap: 10px; width: 100%; padding: 10px 12px;
          background: none; border: none; text-align: left; color: inherit; box-shadow: none;
        }
        .cs-facet-dot {
          flex-shrink: 0; width: 30px; height: 30px; border-radius: 50%; border: 1px solid;
          display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700;
        }
        .cs-facet-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 5px; }
        .cs-facet-line { display: flex; justify-content: space-between; gap: 8px; }
        .cs-facet-name { font-size: 12px; font-weight: 600; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .cs-facet-score { font-size: 12px; color: #9aa5b4; flex-shrink: 0; }
        .cs-bar { display: block; height: 4px; border-radius: 999px; background: rgba(255,255,255,0.08); overflow: hidden; }
        .cs-bar > span { display: block; height: 100%; border-radius: 999px; }
        .cs-chevron { flex-shrink: 0; color: #8793a3; font-size: 12px; transition: transform .2s ease; }
        .cs-detail { padding: 0 14px 14px 52px; }
        .cs-detail-text { margin: 0 0 12px; font-size: 13px; line-height: 1.55; color: #c9d1dc; }
        .cs-actions { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
        .cs-action-primary {
          display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 6px;
          background: transparent; color: #00e1ff; border: 1px solid #00e1ff66; font-size: 12.5px; font-weight: 600; cursor: pointer;
        }
        .cs-action-primary:hover { background: rgba(0,225,255,0.1); }
        .cs-action-link { font-size: 12.5px; color: #36a7f8; text-decoration: none; }
        .cs-action-link:hover { text-decoration: underline; }

        .cs-divider { width: min(1200px, 96vw); margin: 50px auto 0; border: 0; border-top: 1px solid rgba(255,255,255,0.12); }
        .cs-breakdown-heading { margin-top: 36px; }

        @media (max-width: 1000px) {
          .cs-overview-grid { flex-direction: column; align-items: stretch; }
          .cs-side { width: 100%; }
          .cs-radar-card { height: 420px; width: 100%; box-sizing: border-box; }
          .cs-detail { padding-left: 14px; }
          .cs-chip { min-width: 0; flex: 1 1 140px; }
        }
      `}</style>

      <div style={{ padding: '0', maxWidth: '1800px', margin: '0 auto' }}>
        <header className="assessment-header-scores">
          <img src={logo} className="logo-adjustment-score" alt="CoachReady" />
          <h1 className="score-heading">CoachReady Profile Index</h1>
          <h2 style={{ color: 'white', fontSize: '13px', marginBottom: '20px' }}>
            Coaching Effectiveness
          </h2>

          <div className="header-buttons">
            <button
              className="return-dashboard-cross"
              onClick={() =>
                navigate('/CoachTheCoachDashboard', {
                  state: {
                    fromCoachScores: true,
                    ...(viewingChildId && { viewingChildId, childName: viewingChildName }),
                  },
                })
              }
            />
            <button className="print-button" onClick={handlePrint}>
              <FaPrint size={35} />
            </button>
          </div>
        </header>

        <div className="content-container">
          {/* ── SNAPSHOT: overview radar + strengths / focus areas ── */}
          {showSnapshot && (
            <section className="cs-snapshot" aria-label="Coaching profile snapshot">
              <h3 className="cs-section-title">Your Coaching Snapshot</h3>
              <p className="cs-section-sub">
                See the whole picture first, then dig into each category below.
              </p>

              <div className="cs-chips">
                <SummaryChip
                  label="Overall average"
                  value={`${overallAvg.toFixed(1)}/5`}
                  sub={`across ${facets.length} facets`}
                  accent="#00e1ff"
                />
                <SummaryChip
                  label="Strengths"
                  value={strengths.length}
                  sub={`scoring ${STRENGTH_THRESHOLD}+`}
                  accent={STRENGTH_COLOR}
                />
                <SummaryChip
                  label="Focus areas"
                  value={growthAreas.length}
                  sub={`scoring under ${STRENGTH_THRESHOLD}`}
                  accent={GROWTH_COLOR}
                />
              </div>

              <div className="cs-overview-grid">
                <div className="cs-radar-card radar-container">
                  <Radar
                    data={{ labels: visualizations.labels, datasets: visualizations.datasets }}
                    options={getOverviewRadarOptions()}
                  />
                </div>

                   <div className="cs-panel">
                    <p className="cs-panel-title" style={{ color: STRENGTH_COLOR }}>Your Top Strengths</p>
                    <div className="cs-panel-list" style={{ marginTop: '10px' }}>
                      {strengths.length === 0 ? (
                        <p className="cs-empty">No facet has reached {STRENGTH_THRESHOLD} yet. Your focus areas are the best place to start.</p>
                      ) : (
                        strengths.map((f) => (
                          <FacetRow key={f.name} facet={f} accent={STRENGTH_COLOR} expandable={false} />
                        ))
                      )}
                    </div>
                  </div>
                  <div className="invisible-scroll cs-panel">
                    <p className="cs-panel-title" style={{ color: GROWTH_COLOR }}>Focus Areas to Grow</p>
                    <p className="cs-panel-hint">Lowest first. Tap a facet for details and next steps.</p>
                    <div className="cs-panel-list">
                      {growthAreas.length === 0 ? (
                        <p className="cs-empty">Every facet is at {STRENGTH_THRESHOLD} or above. Nice work.</p>
                      ) : (
                        growthAreas.map((f, i) => (
                          <FacetRow
                            key={f.name}
                            facet={f}
                            accent={GROWTH_COLOR}
                            expandable
                            defaultOpen={i === 0}
                            onOpenCoachXcel={openCoachXcel}
                          />
                        ))
                      )}
                    </div>
                  </div>
              </div>

              <hr className="cs-divider" />
              <h3 className="cs-section-title cs-breakdown-heading">Detailed Breakdown by Category</h3>
              <p className="cs-section-sub" style={{ marginBottom: 0 }}>
                Scores and descriptions for every facet, category by category.
              </p>
            </section>
          )}

          {visualizations.labels && visualizations.datasets ? (
            visualizations.datasets.map((dataset, index) => {
              const categoryName = dataset.label || `Coaching Dimension ${index + 1}`;

              const individualData = {
                labels: visualizations.labels,
                datasets: [dataset],
                notesMap: visualizations.notesMap || {},
              };

              return (
                <React.Fragment key={index}>
                  {renderChart(categoryName, individualData)}
                </React.Fragment>
              );
            })
          ) : (
            <div style={{ color: 'white', textAlign: 'center', padding: '60px' }}>
              No valid CoachReady category data found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachScoresPage;